import { useCallback, useRef, useState } from 'react';
import { fetchSasUrl, SasUrlResponse } from '../../../services/api/storage.api';
import { ChatResponseFile } from '../../Chat/types';
import { PreviewFile, mapMimeToPreviewType } from '../types';
import { LoadError, classifyFetchError, classifyReadError, classifySasEndpointStatus } from '../utils/errorClassification';

interface DocPreviewState {
  previewFile: PreviewFile | null;
  isResolving: boolean;
  resolveError: LoadError | null;
}

const INITIAL_STATE: DocPreviewState = { previewFile: null, isResolving: false, resolveError: null };

// Owns the "currently open" document preview: given a file from a chat
// response, resolves it to a fresh SAS URL and holds the result until
// closed or replaced. Doesn't touch fetching/parsing the file's actual
// bytes — that's DocPreviewShell → useDocumentBytes, downstream of this.
export const useDocPreview = (getUserId: () => string) => {
  const [state, setState] = useState<DocPreviewState>(INITIAL_STATE);

  // path of whatever's CURRENTLY successfully shown — reset to null the
  // instant a new attempt starts, set only once that attempt succeeds. This
  // is what lets openFile() skip redundant work; it's deliberately a ref,
  // not read off `state`, so the check can't go stale (see emailRef/
  // userIdRef in Chat.tsx for the same pattern against the same class of
  // problem — a callback frozen at an earlier render still reads the
  // CURRENT value through a ref, where it wouldn't through closed-over state).
  const activePathRef = useRef<string | null>(null);

  // Whichever file openFile was most recently asked to open, success or
  // failure — refreshLink() replays this. Needed because an expired-SAS
  // failure surfaces one layer down, in DocPreviewShell/useDocumentBytes,
  // which only ever sees the already-resolved (now-stale) PreviewFile, not
  // the original ChatResponseFile a fresh SAS lookup needs.
  const lastRequestedFileRef = useRef<ChatResponseFile | null>(null);

  // The link request currently in progress. Opening another file or closing
  // the panel cancels it, so a late reply can never replace a newer file or
  // reopen a closed panel.
  const requestRef = useRef<AbortController | null>(null);

  const openFile = useCallback(async (file: ChatResponseFile, options?: { force?: boolean }) => {
    const type = mapMimeToPreviewType(file.file_type);
    // Not one of the four types this panel renders (e.g. an image) —
    // that's a different, already-shipped pipeline's job, not ours.
    if (!type) return;

    // Already showing this exact file — nothing to do. Skipped when
    // force:true (refreshLink's whole point is re-fetching a file that's
    // already "open" but whose link has gone stale, so it must bypass this).
    if (!options?.force && activePathRef.current === file.path) return;

    requestRef.current?.abort();
    const request = new AbortController();
    requestRef.current = request;

    activePathRef.current = null;
    lastRequestedFileRef.current = file;
    setState({ previewFile: null, isResolving: true, resolveError: null });

    // Mirrors useDocumentBytes's structure: fetch-throw, then !res.ok, then
    // body-parsing are handled as three distinct steps, each reusing the
    // shared error classifiers instead of a single catch-all try/catch —
    // that's what gives the user "likely CORS" / "no access" instead of a
    // raw exception message.
    let res: Response;
    try {
      const userId = getUserId();
      res = await fetchSasUrl(file.path, userId, request.signal);
    } catch (err) {
      if (request.signal.aborted) return; // superseded or closed: not an error
      console.error('[doc-preview] failed to reach the SAS endpoint:', err);
      setState({ previewFile: null, isResolving: false, resolveError: classifyFetchError(err) });
      return;
    }

    if (!res.ok) {
      setState({ previewFile: null, isResolving: false, resolveError: classifySasEndpointStatus(res.status) });
      return;
    }

    try {
      const data: SasUrlResponse = await res.json();
      const fileName = file.file_name ?? file.path.split('/').pop() ?? 'file';

      activePathRef.current = file.path;
      setState({
        previewFile: { path: file.path, sasUrl: data.file_url, type, fileName, contentType: file.file_type },
        isResolving: false,
        resolveError: null,
      });
    } catch (err) {
      if (request.signal.aborted) return;
      console.error('[doc-preview] failed to parse SAS response:', err);
      setState({ previewFile: null, isResolving: false, resolveError: classifyReadError(err) });
    }
  }, [getUserId]);

  // Re-resolves whichever file was last requested — used by both the
  // SAS-lookup-failed retry button and the expired-SAS retry button
  // (DocPreviewShell), so a real error becomes a single click to recover
  // from instead of "close the panel, scroll back up, click Preview again".
  const refreshLink = useCallback(() => {
    if (lastRequestedFileRef.current) {
      void openFile(lastRequestedFileRef.current, { force: true });
    }
  }, [openFile]);

  const closePreview = useCallback(() => {
    requestRef.current?.abort();
    requestRef.current = null;
    activePathRef.current = null;
    lastRequestedFileRef.current = null;
    setState(INITIAL_STATE);
  }, []);

  return { ...state, openFile, refreshLink, closePreview };
};
